import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2, MapPin, Package, Search, ShieldCheck, User } from 'lucide-react';
import { directoryAPI } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import ProductImage from '@/components/brand/ProductImage';

const TYPES = ['farmer', 'manufacturer', 'distributor', 'shopkeeper'];
const PAGE_SIZE = 30;

// Plural labels already used under the photo circles on every home.
const TITLE_KEY = {
  farmer: 'farmerHome.stakeholders.farmers',
  manufacturer: 'farmerHome.stakeholders.manufacturers',
  distributor: 'farmerHome.stakeholders.distributors',
  shopkeeper: 'farmerHome.stakeholders.shopkeepers',
};

function Avatar({ person, size = 'w-12 h-12' }) {
  const initial = (person?.name || '?').trim().charAt(0).toUpperCase();
  return (
    <div className={`${size} rounded-full bg-farm-primary/10 overflow-hidden grid place-items-center flex-shrink-0`}>
      {person?.avatar_url ? (
        <img src={person.avatar_url} alt="" className="w-full h-full object-cover" />
      ) : initial && initial !== '?' ? (
        <span className="font-heading font-bold text-farm-primary">{initial}</span>
      ) : (
        <User className="w-5 h-5 text-farm-primary" aria-hidden />
      )}
    </div>
  );
}

function memberSince(value, language) {
  if (!value) return '';
  const date = new Date(`${value}-01T00:00:00`);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(language, { month: 'long', year: 'numeric' });
}

/**
 * Who is on African Farm, by type. Opened from the four photo circles
 * at the top of every home. The API returns public-safe fields only:
 * no phone numbers, emails or addresses; farmers show a first name and
 * last initial.
 */
export default function DirectoryPage() {
  const { type } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const userType = TYPES.includes(type) ? type : null;

  const [people, setPeople] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(id);
  }, [query]);

  const fetchPage = useCallback(
    (offset) => directoryAPI.list(userType, { limit: PAGE_SIZE, offset, q: debounced || undefined }),
    [userType, debounced],
  );

  useEffect(() => {
    if (!userType) return undefined;
    let cancelled = false;
    setLoading(true);
    fetchPage(0)
      .then((res) => {
        if (cancelled) return;
        setPeople(res.data.users || []);
        setTotal(res.data.total || 0);
        setHasMore(!!res.data.has_more);
      })
      .catch(() => { if (!cancelled) { setPeople([]); setTotal(0); setHasMore(false); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [userType, fetchPage]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const res = await fetchPage(people.length);
      const seen = new Set(people.map((p) => p.id));
      setPeople([...people, ...(res.data.users || []).filter((p) => !seen.has(p.id))]);
      setHasMore(!!res.data.has_more);
    } catch {
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  };

  const openPerson = (person) => {
    setSelected(person);
    setDetail(null);
    directoryAPI.get(userType, person.id)
      .then((res) => setDetail(res.data))
      .catch(() => setDetail(person));
  };

  if (!userType) {
    return (
      <div className="p-4 bg-farm-cream min-h-screen">
        <p className="text-farm-forest/70">{t('directory.unknownType')}</p>
      </div>
    );
  }

  const shown = detail || selected;
  const isBusiness = userType !== 'farmer';

  return (
    <div className="p-4 pt-3 bg-farm-cream min-h-screen" data-testid="directory-page">
      <div className="flex items-center gap-3 mb-4">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-full bg-white grid place-items-center shadow-sm"
          aria-label={t('common.comingSoon.back', { defaultValue: 'Go back' })}
          data-testid="directory-back"
        >
          <ArrowLeft className="w-5 h-5 text-farm-forest" />
        </button>
        <div className="min-w-0">
          <h1 className="font-heading text-2xl font-bold text-farm-forest leading-tight">{t(TITLE_KEY[userType])}</h1>
          <p className="text-sm text-farm-forest/70" data-testid="directory-total">
            {t('directory.total', { count: total })}
          </p>
        </div>
      </div>

      <div className="relative mb-3">
        <Search className="w-4 h-4 text-farm-forest/50 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t(isBusiness ? 'directory.searchBusiness' : 'directory.searchFarmer')}
          className="pl-9 bg-white rounded-full"
          maxLength={60}
          data-testid="directory-search"
        />
      </div>

      <p className="flex items-center gap-1.5 text-xs text-farm-forest/60 mb-3">
        <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0" />
        {t('directory.privacyNote')}
      </p>

      {loading ? (
        <div className="py-12 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-farm-primary" /></div>
      ) : people.length === 0 ? (
        <p className="text-sm text-farm-forest/60 py-8 text-center" data-testid="directory-empty">{t('directory.empty')}</p>
      ) : (
        <ul className="space-y-2" data-testid="directory-list">
          {people.map((person) => (
            <li key={person.id}>
              <button
                type="button"
                onClick={() => openPerson(person)}
                className="w-full bg-white rounded-xl p-3 flex items-center gap-3 text-left shadow-sm hover:shadow focus:outline-none focus:ring-2 focus:ring-farm-primary"
                data-testid="directory-item"
              >
                <Avatar person={person} />
                <span className="flex-1 min-w-0">
                  <span className="block font-heading font-semibold text-farm-forest truncate">{person.name || t('directory.unnamed')}</span>
                  {person.area && (
                    <span className="flex items-center gap-1 text-xs text-farm-forest/60 truncate">
                      <MapPin className="w-3 h-3 flex-shrink-0" /> {person.area}
                    </span>
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {hasMore && !loading && (
        <Button
          variant="outline"
          className="w-full mt-4"
          onClick={loadMore}
          disabled={loadingMore}
          data-testid="directory-load-more"
        >
          {loadingMore ? <Loader2 className="w-4 h-4 animate-spin" /> : t('directory.showMore')}
        </Button>
      )}

      <Dialog open={!!selected} onOpenChange={(open) => { if (!open) { setSelected(null); setDetail(null); } }}>
        <DialogContent className="max-w-md" data-testid="directory-detail">
          <DialogHeader>
            <DialogTitle className="sr-only">{shown?.name}</DialogTitle>
          </DialogHeader>
          {shown && (
            <div>
              <div className="flex items-center gap-4 mb-4">
                <Avatar person={shown} size="w-16 h-16" />
                <div className="min-w-0">
                  <p className="font-heading text-xl font-bold text-farm-forest leading-tight">{shown.name || t('directory.unnamed')}</p>
                  <p className="text-sm text-farm-forest/70">{t(`directory.role.${userType}`)}</p>
                </div>
              </div>

              <dl className="space-y-3 text-sm">
                {(shown.states?.length > 0 || shown.area) && (
                  <div>
                    <dt className="text-farm-forest/60 text-xs">{t('directory.area')}</dt>
                    <dd className="text-farm-forest">
                      {shown.states?.length > 0 ? shown.states.join(', ') : shown.area}
                    </dd>
                  </div>
                )}
                {shown.location && (
                  <div>
                    <dt className="text-farm-forest/60 text-xs">{t('directory.location')}</dt>
                    <dd className="text-farm-forest">{shown.location}</dd>
                  </div>
                )}
                {shown.member_since && (
                  <div>
                    <dt className="text-farm-forest/60 text-xs">{t('directory.memberSince')}</dt>
                    <dd className="text-farm-forest">{memberSince(shown.member_since, i18n.language)}</dd>
                  </div>
                )}
                {typeof shown.product_count === 'number' && (
                  <div>
                    <dt className="text-farm-forest/60 text-xs">
                      {t(userType === 'distributor' ? 'directory.productsStocked' : 'directory.products')}
                    </dt>
                    <dd className="text-farm-forest font-semibold" data-testid="directory-product-count">{shown.product_count}</dd>
                  </div>
                )}
              </dl>

              {shown.products?.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mt-4" data-testid="directory-products">
                  {shown.products.map((p) => (
                    <button
                      type="button"
                      key={p.id}
                      onClick={() => navigate(`/marketplace?product=${p.id}`)}
                      className="text-left focus:outline-none focus:ring-2 focus:ring-farm-primary rounded-lg"
                    >
                      <div className="h-20 rounded-lg overflow-hidden"><ProductImage product={p} /></div>
                      <p className="text-xs text-farm-forest mt-1 line-clamp-2">{p.name}</p>
                    </button>
                  ))}
                </div>
              )}

              {!detail && (
                <div className="flex items-center gap-2 text-xs text-farm-forest/60 mt-4">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> {t('directory.loading')}
                </div>
              )}

              {userType === 'manufacturer' && shown.product_count > 0 && (
                <p className="flex items-center gap-1.5 text-xs text-farm-forest/60 mt-3">
                  <Package className="w-3.5 h-3.5" /> {t('directory.productsHint')}
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
